<template>
    <panels-wrapper>
        <mobile-panel-wrapper :is-open="isOpen" :element-height="elementHeight" class="items-center">
            <div
                class="text-3xl uppercase flex"
                :class="productLayers.length < 2 && 'mb-5'">
                {{selectedPlacement.custom?.['v-title'] || selectedPlacement.name}} Color
            </div>
            <div
                :class="['flex flex-col items-center justify-around w-full transition-all ease-in-out duration-500 overflow-hidden max-w-lg mx-auto',
                         subScreen === 'lid-color' || subScreen === 'body-color' || subScreen === 'base-color' ? 'h-16' : 'h-0'
                ]">
                <color-options
                    :xAxisScroll="true"
                    class="my-6 mb-4"
                    :availableColorways="availableColors"
                    @selectColorway="handleSelectColorway"
                />
            </div>
            <div class="max-w-lg mx-auto w-full" v-if="productLayers.length > 1">
                <horizontal-divider class="w-full" />
                <horizontal-scroll-outer-wrapper class="py-1">
                    <horizontal-scroll-inner-wrapper>
                        <button
                            class="flex justify-center items-center cursor-pointer w-24 space-x-2 mb-2"
                            v-for="productComponent in productLayers"
                            :key="productComponent.placement.code"
                            @click="handleSelectStep('colors', productComponent)"

                        >
                            <div>{{productComponent.title}}</div>
                        </button>
                    </horizontal-scroll-inner-wrapper>
                </horizontal-scroll-outer-wrapper>
            </div>
            <div class="flex flex-col flex-1"></div>
            <action-buttons-wrapper>
                <secondary-button
                    @click="cancel">
                    {{ $t('ACTION_CANCEL') }}
                </secondary-button>
                <primary-button @click="accept">
                    {{ $t('ACTION_SAVE') }}
                </primary-button>
            </action-buttons-wrapper>
        </mobile-panel-wrapper>
    </panels-wrapper>
</template>

<script>
    import { mapActions, mapState, mapGetters } from 'vuex';
    import ColorsPanel from '../ColorsPanel.vue';
    import MobilePanelWrapper from './MobilePanelWrapper.vue';
    import PanelsWrapper from '../PanelsWrapper.vue';
    import { getAvailableColorways, getAvailablePrintingMethods, getSupportedPrintingModes } from '../../../utilities/colors';
    import { showQuantityWarningConfirmationModal } from '../../../utilities/UI/modals';
    import ColorOptions from '../../colors/ColorOptions.vue';
    import ActionButtonsWrapper from '../../buttons/ActionButtonsWrapper.vue';
    import PrimaryButton from '../../buttons/PrimaryButton.vue';
    import SecondaryButton from '../../buttons/SecondaryButton.vue';
    import HorizontalScrollInnerWrapper from '../../HorizontalScrollInnerWrapper.vue';
    import HorizontalScrollOuterWrapper from '../../HorizontalScrollOuterWrapper.vue';
    import HorizontalDivider from '../../HorizontalDivider.vue';
    import { fillEmptyDivs } from '../../../utils';

    export default {
        components: {
            ColorsPanel,
            MobilePanelWrapper,
            PanelsWrapper,
            ColorOptions,
            ActionButtonsWrapper,
            PrimaryButton,
            SecondaryButton,
            HorizontalScrollInnerWrapper,
            HorizontalScrollOuterWrapper,
            HorizontalDivider,
        },
        data() {
            return {
                subScreen: 'lid-color',
            };
        },
        props: {
            isOpen: {
                type: Boolean,
                default: false,
            },
            elementHeight: {
                type: String,
                required: false,
            },
        },
        computed: {
            ...mapState({
                selectedStep: (state) => state.selectedStep,
                selectedStepParameters: (state) => state.selectedStepParameters,
                viewController: (state) => state.customizer.viewController,
                selectedPersonalizationMethod: (state) => state.customizer.personalizationMethod,
                productData: (state) => state.ecommerce.products,
                secondaryBottles: (state) => state.customizer.secondaryBottles,
                quantity: (state) => state.customizer.quantity,
            }),
            ...mapGetters({
                colorStepCaption: 'colorStepTitle',
                selectedColor: 'selectedColor',
                selectedColorHex: 'selectedColorHex',
                productLayers: 'productLayers',
            }),

            selectedPlacement() {
                return this.selectedStepParameters?.placement;
            },

            availableColors() {
                return getAvailableColorways({
                    viewController: this.viewController,
                    placement: this.selectedPlacement,
                    productData: this.productData,
                });
            },
            /**
             * Gets description of printing methods for small screens.
             */
            methodsDescriptionSmall() {
                return this.viewController ? this.$t('METHODS_SMALL') : '';
            },

            availablePrintingOptions() {
                return getAvailablePrintingMethods({
                    viewController: this.viewController,
                    availableColors: this.availableColors,
                    selectedPersonalizationMethod: this.selectedPersonalizationMethod,
                    $t: this.$t.bind(this),
                });
            },

            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },
        },
        mounted() {
            // When the component is loaded, capture the current state of the view controller and save it in case
            // we need to revert to the previous state and toss away all customizations.
            //
            // At any given time there should be only one module that can do the revert.
            this.handleSelectStep('colors', this.productLayers[0]);
            this.captureController();
            fillEmptyDivs();
        },
        methods: {
            ...mapActions({
                setController: 'showController',
                setPersonalizationMethod: 'setPersonalizationMethod',
                selectStep: 'selectStep',
                setQuantity: 'setQuantity',
                setSecondaryBottles: 'setSecondaryBottles',
                showGenericConfirm: 'showGenericConfirm',
            }),

            handleSelectStep(step, parameters) {
                if (this.isGroupOrder) {
                    // Switch to the primary bottle first.
                    if (this.secondaryBottles.indexOf(this.selectedBottle) !== 0) {
                        this.$store.dispatch('setSelectedBottle', this.secondaryBottles[0]).then(() => {
                            this.selectStep({ step, parameters });
                        });
                    } else {
                        this.selectStep({ step, parameters });
                    }
                } else {
                    this.selectStep({ step, parameters });
                }
            },
            handleSelectColorway(colorway) {
                const quantity = this.isGroupOrder ? this.secondaryBottles.length : this.quantity;

                const { ats: availableToSell } = colorway;

                if (availableToSell === 0) {
                    return;
                }

                // Inventory popup
                if (availableToSell >= quantity) {
                    this.reducedQuantity = null;
                    this.updatePersonalizationMethod(null, colorway);
                } else {
                    showQuantityWarningConfirmationModal({
                        showGenericConfirm: this.showGenericConfirm,
                        colorway,
                        callback: () => {
                            this.reducedQuantity = availableToSell;
                            this.updatePersonalizationMethod(availableToSell, colorway);
                        },
                    });
                }
            },

            captureController() {
                if (!this.viewController) {
                    return;
                }

                this.backupViewController = this.viewController.clone({ excludeEvents: false });
                this.backupPersonalizationMethod = this.selectedPersonalizationMethod;
            },

            goToMainScreen() {
                this.selectStep(null);
            },

            accept() {
                if (this.reducedQuantity != null) {
                    if (this.isGroupOrder) {
                        this.setSecondaryBottles(this.secondaryBottles.slice(0, this.reducedQuantity));
                    } else {
                        this.setQuantity(this.reducedQuantity);
                    }
                }

                this.goToMainScreen(null);
            },

            revert() {
                this.setController(this.backupViewController);
                this.setPersonalizationMethod(this.backupPersonalizationMethod);
            },

            cancel() {
                this.showGenericConfirm({
                    title: this.$t('CANCEL_CONFIRMATION'),
                    description: this.$t('CANCEL_CONFIRMATION_BODY'),
                    cancelAction: this.$t('CANCEL_CONFIRMATION_CANCEL'),
                    confirmAction: this.$t('CANCEL_CONFIRMATION_OK'),
                    confirmationCallback: () => {
                        this.revert();
                        this.goToMainScreen();
                    },
                });
            },

            updatePersonalizationMethod(reducedQuantity, colorway) {
                const { supportsEtching, supportsPrinting } = getSupportedPrintingModes(
                    this.viewController.findComponent(colorway.option.code),
                );

                const needsSwitchToEtching = this.selectedPersonalizationMethod === 'printing' && !supportsPrinting;
                const needsSwitchToPrinting = this.selectedPersonalizationMethod === 'etching' && !supportsEtching;

                // Handling printing type switching ( also reset / set cut quantity if needed)
                if (needsSwitchToEtching || needsSwitchToPrinting) {
                    const confirmModal = needsSwitchToEtching ? this.switchToEtchingConfirm : this.switchToPrintingConfirm;
                    const newPersonalizationMethod = this.selectedPersonalizationMethod === 'printing' ? 'etching' : 'printing';

                    confirmModal(() => {
                        this.reducedQuantity = reducedQuantity;
                        this.setPersonalizationMethod(newPersonalizationMethod);
                        this.viewController.updateComponent(this.selectedPlacement.code, colorway.option.code);
                    });
                } else {
                    this.viewController.updateComponent(this.selectedPlacement.code, colorway.option.code);
                }
            },
        },
    };
</script>
